import FJPoolStore from '../FJPoolStore';
import { FJCssPerspectiveToWebGLStore } from './FJCssPerspectiveToWebGLStore';

const MAX_PRIVATE_STORES = 1;
const MAX_PUBLIC_STORES = 10;
const MAX_SIZED_CANVAS_POOL_ENTRIES = 11; // 池中canvas的最大数量

// Define size tiers for the targetCanvas. The smallest tier that can contain the sourceCanvas will be chosen.
// These should be in ascending order.
const TARGET_CANVAS_SIZE_TIERS = [256, 360, 512, 720, 1080, 1280, 1440, 1600, 1920, 2560, 3840];

class FJCssPerspectiveToWebGLManager {
    constructor() {
        // publicTargetCanvas is removed. All target canvases come from sizedCanvasPool.
        this.sizedCanvasPool = new Map();

        this.publicStores = new Map();
        this.privateStores = new Map();
    }

    /**
     * Determines the appropriate target canvas size from predefined tiers
     * based on the source canvas dimensions.
     * @param {number} sourceWidth The width of the source canvas.
     * @param {number} sourceHeight The height of the source canvas.
     * @returns {{width: number, height: number}} The chosen tiered size.
     * @private
     */
    _getTieredTargetSize(sourceWidth, sourceHeight) {
        const ASPECT_RATIOS = {
            '1:1': 1 / 1,
            '16:9': 16 / 9,
            '9:16': 9 / 16,
            '21:9': 21 / 9,
            '3:4': 3 / 4,
            '2:1': 2 / 1,
        };

        let bestFit = null;

        for (const tierWidth of TARGET_CANVAS_SIZE_TIERS) {
            for (const arName in ASPECT_RATIOS) {
                if (Object.prototype.hasOwnProperty.call(ASPECT_RATIOS, arName)) {
                    const arValue = ASPECT_RATIOS[arName];
                    const currentTargetWidth = tierWidth;
                    const currentTargetHeight = Math.round(currentTargetWidth / arValue);

                    if (currentTargetWidth >= sourceWidth && currentTargetHeight >= sourceHeight) {
                        // This candidate can contain the source canvas
                        const currentArea = currentTargetWidth * currentTargetHeight;
                        if (
                            bestFit === null ||
                            currentArea < bestFit.area ||
                            (currentArea === bestFit.area && currentTargetWidth < bestFit.width) ||
                            (currentArea === bestFit.area &&
                                currentTargetWidth === bestFit.width &&
                                currentTargetHeight < bestFit.height)
                        ) {
                            bestFit = {
                                width: currentTargetWidth,
                                height: currentTargetHeight,
                                area: currentArea,
                                // arName: arName, // Store if needed by other parts of the code
                            };
                        }
                    }
                }
            }
        }

        if (bestFit) {
            return { width: bestFit.width, height: bestFit.height };
        } else {
            return { width: sourceWidth, height: sourceHeight };
        }
    }

    _getOrAllocatePublicStore(sizeKey) {
        if (this.publicStores.has(sizeKey)) {
            const storeInstance = this.publicStores.get(sizeKey);

            // 检测 WebGL 上下文是否丢失
            if (storeInstance.isContextLost()) {
                console.warn(`[WebGL]:Public store WebGL context lost, recreating store for size: ${sizeKey}`);
                storeInstance.destroy();
                this.publicStores.delete(sizeKey);
                const newStore = new FJCssPerspectiveToWebGLStore();
                this.publicStores.set(sizeKey, newStore);
                return newStore;
            }

            // Context is fine, move to end for LRU
            this.publicStores.delete(sizeKey);
            this.publicStores.set(sizeKey, storeInstance);
            return storeInstance;
        } else {
            // No exact match for sizeKey
            if (this.publicStores.size >= MAX_PUBLIC_STORES) {
                // Pool is full, reuse the LRU store
                const lruKey = this.publicStores.keys().next().value;
                const storeToReuse = this.publicStores.get(lruKey);

                // 检测要重用的 store 的 WebGL 上下文是否丢失
                if (storeToReuse.isContextLost()) {
                    console.warn(`[WebGL]:LRU store WebGL context lost, recreating store for size: ${sizeKey}`);
                    // 销毁旧的 store
                    storeToReuse.destroy();
                    // 创建新的 store
                    const newStore = new FJCssPerspectiveToWebGLStore();
                    this.publicStores.delete(lruKey); // Remove old entry
                    this.publicStores.set(sizeKey, newStore); // Add with new key, becomes most recent
                    return newStore;
                }

                this.publicStores.delete(lruKey); // Remove old entry
                this.publicStores.set(sizeKey, storeToReuse); // Add with new key, becomes most recent
                return storeToReuse;
            } else {
                // Pool is not full, create a new store
                const newStore = new FJCssPerspectiveToWebGLStore();
                this.publicStores.set(sizeKey, newStore); // Add with new key, becomes most recent
                return newStore;
            }
        }
    }

    /**
     * Retrieves or allocates a canvas from the sizedCanvasPool based on target size.
     * Implements LRU policy if the pool is full.
     * @param {{width: number, height: number}} targetSize - The desired width and height of the canvas.
     * @returns {HTMLCanvasElement} The canvas element.
     * @private
     */
    _getOrAllocateSizedCanvas(targetSize) {
        const sizeKey = `${targetSize.width}x${targetSize.height}`;
        let canvas;

        if (this.sizedCanvasPool.has(sizeKey)) {
            canvas = this.sizedCanvasPool.get(sizeKey);
            // Move to end of map to mark as most recently used
            this.sizedCanvasPool.delete(sizeKey);
            this.sizedCanvasPool.set(sizeKey, canvas);
            // Ensure dimensions are correct (safety check)
            if (canvas.width !== targetSize.width || canvas.height !== targetSize.height) {
                canvas.width = targetSize.width;
                canvas.height = targetSize.height;
            }
        } else {
            // Canvas of this size not in pool
            if (this.sizedCanvasPool.size >= MAX_SIZED_CANVAS_POOL_ENTRIES) {
                // Pool is full, evict LRU (first entry in map iteration)
                const lruKey = this.sizedCanvasPool.keys().next().value;
                canvas = this.sizedCanvasPool.get(lruKey);
                this.sizedCanvasPool.delete(lruKey);

                // Resize and reuse for the new sizeKey
                canvas.width = targetSize.width;
                canvas.height = targetSize.height;
                this.sizedCanvasPool.set(sizeKey, canvas); // Becomes most recently used
            } else {
                // Pool is not full, create a new canvas
                canvas = FJPoolStore.getCanvas();
                canvas.width = targetSize.width;
                canvas.height = targetSize.height;
                this.sizedCanvasPool.set(sizeKey, canvas); // Becomes most recently used
            }
        }
        return canvas;
    }

    /**
     * Retrieves a store instance.
     *
     * @param {object} [options={}] - Configuration for store retrieval.
     * @param {string} [options.id] - The unique identifier for a private store.
     *                                This is required if 'usePublic' is false.
     * @param {boolean} [options.usePublic=true] - Intention to use a public store. If true, a public store based on size is returned.
     *                                             If false, attempts to retrieve/create/reuse a private store.
     * @param {number} [options.sourceWidth] - The width of the source canvas. Required if 'usePublic' is true,
     *                                         OR if 'usePublic' is false (to form part of the private store\'s unique key).
     * @param {number} [options.sourceHeight] - The height of the source canvas. Required under the same conditions as 'sourceWidth'.
     * @returns {{store: FJCssPerspectiveToWebGLStore, isPublic: boolean, sizeKey?: string}} An object containing the store instance,
     *                                                                  a boolean indicating if the returned store is a public one,
     *                                                                  and optionally the sizeKey used.
     * @throws {Error} If 'usePublic' is false and no 'id' is provided or id is invalid.
     * @throws {Error} If a public store or a private store is needed and `sourceWidth`/`sourceHeight` are not provided or invalid.
     * @throws {Error} If attempting to create a new private store when MAX_PRIVATE_STORES is 0 and usePublic is false.
     */
    getStore({ id, usePublic = true, targetSize } = {}) {
        if (usePublic) {
            if (typeof targetSize.width !== 'number' || typeof targetSize.height !== 'number') {
                throw new Error(
                    'sourceWidth and sourceHeight (numbers) must be provided when requesting a public store.',
                );
            }
            const sizeKey = `${targetSize.width}x${targetSize.height}`;
            const publicStoreInstance = this._getOrAllocatePublicStore(sizeKey);
            return { store: publicStoreInstance, isPublic: true, sizeKey };
        }

        // Logic for private stores with lastUsedTime and reuse, with fallback to public
        if (typeof id === 'undefined' || id === null || String(id).trim() === '') {
            throw new Error('An ID must be provided when requesting a private store (usePublic is false).');
        }

        const sizeKeyForPrivate = `${targetSize.width}x${targetSize.height}`;
        const compositeKey = `${id}`;

        if (this.privateStores.has(compositeKey)) {
            const entry = this.privateStores.get(compositeKey);

            // 检测私有 store 的 WebGL 上下文是否丢失
            if (entry.store.isContextLost()) {
                console.log(`[WebGL]:Private store WebGL context lost, recreating store for id: ${id}`);
                // 销毁旧的 store
                entry.store.destroy();
                // 从池中删除
                this.privateStores.delete(compositeKey);
                // 创建新的 store
                const newStoreInstance = new FJCssPerspectiveToWebGLStore();
                const newEntry = { store: newStoreInstance, lastUsedTime: Date.now() };
                this.privateStores.set(compositeKey, newEntry);
                return { store: newStoreInstance, isPublic: false, sizeKey: sizeKeyForPrivate };
            }

            entry.lastUsedTime = Date.now();
            this.privateStores.delete(compositeKey); // For LRU behavior in Map
            this.privateStores.set(compositeKey, entry);
            return { store: entry.store, isPublic: false, sizeKey: sizeKeyForPrivate };
        } else {
            if (MAX_PRIVATE_STORES === 0) {
                throw new Error(
                    'Private store limit (MAX_PRIVATE_STORES) is 0. Cannot create private stores when usePublic is false.',
                );
            }

            if (this.privateStores.size < MAX_PRIVATE_STORES) {
                const newStoreInstance = new FJCssPerspectiveToWebGLStore();
                const newEntry = { store: newStoreInstance, lastUsedTime: Date.now() };
                this.privateStores.set(compositeKey, newEntry);
                return { store: newStoreInstance, isPublic: false, sizeKey: sizeKeyForPrivate };
            } else {
                // Pool is full, try to find a private store to reuse (older than 2s)
                let lruCompositeKeyToReuse = null;
                for (const [currentStoreCompositeKey, currentEntry] of this.privateStores.entries()) {
                    if (Date.now() - currentEntry.lastUsedTime > 2000) {
                        lruCompositeKeyToReuse = currentStoreCompositeKey;
                        break;
                    }
                }

                if (lruCompositeKeyToReuse) {
                    // Reuse an available private store
                    const entryToReuse = this.privateStores.get(lruCompositeKeyToReuse);

                    // 检测要重用的私有 store 的 WebGL 上下文是否丢失
                    if (entryToReuse.store.isContextLost()) {
                        console.log(`[WebGL]:Reused private store WebGL context lost, recreating store for id: ${id}`);
                        // 销毁旧的 store
                        entryToReuse.store.destroy();
                        // 从池中删除旧的
                        this.privateStores.delete(lruCompositeKeyToReuse);
                        // 创建新的 store
                        const newStoreInstance = new FJCssPerspectiveToWebGLStore();
                        const newEntry = { store: newStoreInstance, lastUsedTime: Date.now() };
                        this.privateStores.set(compositeKey, newEntry);
                        return { store: newStoreInstance, isPublic: false, sizeKey: sizeKeyForPrivate };
                    }

                    this.privateStores.delete(lruCompositeKeyToReuse);
                    entryToReuse.lastUsedTime = Date.now(); // Update its usage time
                    this.privateStores.set(compositeKey, entryToReuse); // Store under the new composite key
                    return { store: entryToReuse.store, isPublic: false, sizeKey: sizeKeyForPrivate };
                } else {
                    const publicStoreInstance = this._getOrAllocatePublicStore(sizeKeyForPrivate);
                    return { store: publicStoreInstance, isPublic: true, sizeKey: sizeKeyForPrivate };
                }
            }
        }
    }

    /**
     * Gets the current number of active private stores.
     * @returns {number} The number of private stores.
     */
    getActivePrivateStoreCount() {
        return this.privateStores.size;
    }

    /**
     * Gets the shared public store instance directly based on source dimensions.
     * This is a convenience method.
     * @param {object} [options={}] - Options object.
     * @param {number} options.sourceWidth - The width of the source canvas.
     * @param {number} options.sourceHeight - The height of the source canvas.
     * @returns {FJCssPerspectiveToWebGLStore} The public store for the given size.
     * @throws {Error} If sourceWidth or sourceHeight are not provided or invalid.
     */
    getPublicStore({ sourceWidth, sourceHeight } = {}) {
        if (typeof sourceWidth !== 'number' || typeof sourceHeight !== 'number') {
            throw new Error('sourceWidth and sourceHeight (numbers) must be provided to get a public store.');
        }
        const targetSize = this._getTieredTargetSize(sourceWidth, sourceHeight);
        const sizeKey = `${targetSize.width}x${targetSize.height}`;
        return this._getOrAllocatePublicStore(sizeKey);
    }

    /**
     * 将被绘制到这个特定存储画布的中心。
     * @param {object} options - 包含所有转换参数的对象。
     * @param {HTMLCanvasElement} options.canvas - 源画布。
     * @param {string} [options.id] - 要使用的私有存储的ID。如果`options.usePublic`为false，则此参数是必需的。
     * @param {boolean} [options.usePublic=false] - 是否使用公共存储。默认为false,公共存储会每个画面都重新绑定纹理，如果没有每个画面都更改建议使用私有存储。
     * @throws {Error} 如果无法找到/访问存储，如果存储的`convertCssPerspectiveToWebGL`方法不存在，
     *                或者如果`options.canvas`无效。
     */
    convertCssPerspectiveToWebGL(options) {
        const { id, usePublic = false, isRebindTexture = false, ...conversionData } = options;

        const sourceCanvas = conversionData.canvas;
        if (
            !sourceCanvas ||
            typeof sourceCanvas.getContext !== 'function' ||
            !(sourceCanvas instanceof HTMLCanvasElement)
        ) {
            throw new Error('Invalid or missing source canvas in options.canvas. It must be an HTMLCanvasElement.');
        }

        const targetSize = this._getTieredTargetSize(sourceCanvas.width, sourceCanvas.height);
        const storeResult = this.getStore({
            id,
            usePublic,
            targetSize,
        });

        const storeInstance = storeResult.store;
        // 公共存储必定需要重新绑定纹理，私有存储如果需要重新绑定纹理，则传递  isRebindTexture === true
        const effectivelyUsingPublic = storeResult.isPublic || isRebindTexture;

        if (typeof storeInstance.convertCssPerspectiveToWebGL !== 'function') {
            throw new Error(
                `The method 'convertCssPerspectiveToWebGL' does not exist on the retrieved FJCssPerspectiveToWebGLStore instance.`,
            );
        }

        const targetCanvas = this._getOrAllocateSizedCanvas(targetSize);

        const targetCtx = targetCanvas.getContext('2d');

        targetCtx.clearRect(0, 0, targetCanvas.width, targetCanvas.height);
        const dx = (targetCanvas.width - sourceCanvas.width) / 2;
        const dy = (targetCanvas.height - sourceCanvas.height) / 2;
        targetCtx.drawImage(sourceCanvas, dx, dy, sourceCanvas.width, sourceCanvas.height);

        const updatedConversionData = {
            ...conversionData,
            canvas: targetCanvas,
        };

        storeInstance.convertCssPerspectiveToWebGL({ ...updatedConversionData, rebindTexture: effectivelyUsingPublic });

        const sourceCtx = sourceCanvas.getContext('2d');

        if (!sourceCtx) {
            console.log(
                '[WebGL]:Could not get 2D context from the original sourceCanvas to draw back the modified content.',
            );
            return;
        }

        sourceCtx.clearRect(0, 0, sourceCanvas.width, sourceCanvas.height);
        sourceCtx.drawImage(
            targetCanvas,
            dx,
            dy,
            sourceCanvas.width,
            sourceCanvas.height,
            0,
            0,
            sourceCanvas.width,
            sourceCanvas.height,
        );
    }

    /**
     * Checks for private store availability.
     * 1. If the private store pool is not full (size < MAX_PRIVATE_STORES), returns `true`
     *    (space is available for the given ID, either new or existing).
     * 2. If the pool is full:
     *    a. If a store with the given `id` and size (derived from `sourceWidth`, `sourceHeight`)
     *       already exists in the pool, returns `true`.
     *    b. If a store with the given `id` and size does not exist, checks if any *other* store
     *       in the full pool is idle (not used in the last 2000ms and can be reused).
     *       Returns `true` if an idle store is found, `false` otherwise.
     * @param {object} options - Options object.
     * @param {string} options.id - The ID of the private store to check/potentially use.
     * @param {number} options.sourceWidth - The width of the source canvas to determine the size component of the private store.
     * @param {number} options.sourceHeight - The height of the source canvas to determine the size component of the private store.
     * @returns {boolean} True if a private store is available according to the above logic, false otherwise.
     */
    hasPrivateStore(id) {
        if (typeof id === 'undefined' || id === null || String(id).trim() === '') {
            return false;
        }

        if (MAX_PRIVATE_STORES === 0) return false;

        const compositeKey = id;

        if (this.privateStores.size < MAX_PRIVATE_STORES) {
            return true; // Space is available for a new store (including this specific one)
        }

        if (this.privateStores.has(compositeKey)) {
            const entry = this.privateStores.get(compositeKey);
            if (entry.store.isContextLost()) {
                return false;
            }
            return true;
        }

        // If we reach here, either:
        // 1. The specific store (compositeKey) does not exist.
        // 2. The specific store exists but is timed out (and thus considered "available for reuse" like any other idle store).
        // In either case, check if *any* store in the pool is idle.
        for (const entry of this.privateStores.values()) {
            if (Date.now() - entry.lastUsedTime > 2000 && !entry.store.isContextLost()) {
                // Found an idle store
                return true; // An idle store is available for reuse
            }
        }

        return false; // Pool is full, no specific active store, and no idle stores to reuse.
    }
}

const instance = new FJCssPerspectiveToWebGLManager();

export default instance;
